import { formatCalendarDayMonthPtBr } from '@/components/faturas/dates';
import type { ScreenFatura } from './types';

/**
 * Which of the two platform-estimated invoice shapes a fatura is (see
 * `CreditCardInvoice.isEstimated`): `'paid'` is the estimated CLOSED bill
 * (settled by the detected payment), `'open'` the estimated OPEN cycle that
 * starts the day after the estimated close. `null` for real bank invoices.
 */
export type FaturaEstimateKind = null | 'paid' | 'open';

export function faturaEstimateKind(
  fatura: Pick<ScreenFatura, 'isEstimated' | 'source'>,
): FaturaEstimateKind {
  if (!fatura.isEstimated) return null;
  return fatura.source.isOpen ? 'open' : 'paid';
}

/** One-line pill subline replacing the usual "N lanc. · vence dd mmm". */
export function faturaEstimateSubline(fatura: ScreenFatura): string | null {
  const kind = faturaEstimateKind(fatura);
  if (kind === null) return null;

  if (kind === 'paid') {
    const paidAt = formatCalendarDayMonthPtBr(fatura.paidAt);
    return paidAt
      ? `Estimada pelo pagamento de ${paidAt} · aguardando o banco`
      : 'Estimada pelo pagamento · aguardando o banco';
  }

  const closeDate = formatCalendarDayMonthPtBr(fatura.estimatedCloseDate);
  return closeDate ? `Estimativa · lançamentos após ${closeDate}` : 'Estimativa';
}

/** Drawer explainer paragraph; `fmt` is the caller's currency formatter (privacy-aware). */
export function faturaEstimateExplainer(
  fatura: ScreenFatura,
  fmt: (value: number) => string,
): string | null {
  const kind = faturaEstimateKind(fatura);
  if (kind === null) return null;

  const closeDate = formatCalendarDayMonthPtBr(fatura.estimatedCloseDate);
  if (kind === 'paid') {
    const amount = fmt(fatura.paidAmount ?? fatura.total);
    const closing = closeDate ? `até o fechamento de ${closeDate}` : 'até o fechamento';
    return `Fatura estimada pelo pagamento de ${amount}: o banco ainda não enviou a fatura fechada. `
      + `Os lançamentos abaixo são os estimados ${closing} e podem não somar o total.`;
  }

  const after = closeDate ? `os lançamentos após ${closeDate}` : 'os lançamentos após o fechamento estimado';
  return 'Fatura estimada: o banco ainda não enviou a fatura fechada. '
    + `O valor considera ${after} e pode mudar quando a fatura real chegar.`;
}
